import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Plus, Pencil, Trash2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", slug: "", logo_url: "", description: "", sort_order: 0, active: true };

export default function AdminBrands() {
    const [list, setList] = useState([]);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/brands").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            const payload = { ...data, sort_order: Number(data.sort_order || 0) };
            if (data.id) await api.put(`/admin/brands/${data.id}`, payload);
            else await api.post("/admin/brands", payload);
            toast.success("Marca guardada");
            setEdit(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminar marca?")) return;
        await api.delete(`/admin/brands/${id}`);
        load();
    };
    const syncFromProducts = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/brands/sync-from-products");
            toast.success(`Importadas ${data.created} novas marcas (total: ${data.total})`);
            load();
        } catch (e) {
            toast.error("Erro a sincronizar");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-brands-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-black text-4xl tracking-tighter">Marcas</h1>
                    <p className="text-sm text-ink/60 mt-1">{list.length} marcas no sistema. Auto-importadas dos produtos.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={syncFromProducts} disabled={busy} className="btn-secondary" data-testid="sync-brands-btn">
                        <RefreshCw size={16} className={busy ? "animate-spin" : ""} /> Importar marcas dos produtos
                    </button>
                    <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary" data-testid="new-brand-btn">
                        <Plus size={16} /> Nova marca
                    </button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((b) => (
                    <div key={b.id} className="border-2 border-ink bg-white p-4 flex items-start gap-4" data-testid={`brand-card-${b.slug}`}>
                        <div className="w-16 h-16 border-2 border-ink/20 flex items-center justify-center bg-paper shrink-0">
                            {b.logo_url ? (
                                <img src={b.logo_url} alt={b.name} className="w-full h-full object-contain" />
                            ) : (
                                <span className="font-display font-black text-xl text-magenta">{b.name.slice(0, 2).toUpperCase()}</span>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-display font-black text-lg truncate">{b.name}</h3>
                            <div className="font-mono text-[11px] text-ink/50">{b.slug}</div>
                            {!b.active && <span className="inline-block mt-1 text-[10px] font-mono bg-ink/10 px-2 py-0.5">INACTIVA</span>}
                        </div>
                        <div className="flex flex-col gap-1">
                            <button onClick={() => setEdit(b)} className="p-1 hover:text-magenta">
                                <Pencil size={14} />
                            </button>
                            <button onClick={() => del(b.id)} className="p-1 hover:text-destructive">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {edit && <BrandModal initial={edit} onClose={() => setEdit(null)} onSave={save} />}
        </div>
    );
}

function BrandModal({ initial, onClose, onSave }) {
    const [f, setF] = useState(initial);
    const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    return (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="brand-modal">
            <div className="bg-white border-2 border-ink w-full max-w-xl shadow-[8px_8px_0_0_#0A0A0A] my-10">
                <div className="p-5 border-b-2 border-ink flex justify-between">
                    <h2 className="font-display font-black text-2xl">{f.id ? "Editar marca" : "Nova marca"}</h2>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="p-6 space-y-4">
                    <label className="block">
                        <span className="label-mono mb-2 block">Nome *</span>
                        <input value={f.name} onChange={upd("name")} required className="input-edge" data-testid="brand-name-input" />
                    </label>
                    <label className="block">
                        <span className="label-mono mb-2 block">Slug (URL)</span>
                        <input value={f.slug || ""} onChange={upd("slug")} placeholder="auto a partir do nome" className="input-edge" />
                    </label>
                    <div>
                        <span className="label-mono mb-2 block">Logótipo</span>
                        <FileUpload value={f.logo_url} onChange={(v) => setF((s) => ({ ...s, logo_url: v }))} label="" />
                    </div>
                    <label className="block">
                        <span className="label-mono mb-2 block">Descrição</span>
                        <textarea value={f.description || ""} onChange={upd("description")} rows={3} className="input-edge" />
                    </label>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="label-mono mb-2 block">Ordem</span>
                            <input type="number" value={f.sort_order || 0} onChange={upd("sort_order")} className="input-edge" />
                        </label>
                        <label className="flex items-center gap-2 mt-7">
                            <input type="checkbox" checked={!!f.active} onChange={upd("active")} className="accent-magenta w-4 h-4" />
                            <span className="text-sm font-bold uppercase">Activa</span>
                        </label>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t-2 border-ink">
                        <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                        <button type="submit" className="btn-primary" data-testid="save-brand-btn">Guardar</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
